import * as React from "react"
import { AlertTriangleIcon, BanIcon, FileWarningIcon, ScaleIcon, Undo2Icon, type LucideIcon } from "lucide-react"

import { Badge, cn } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useFinanceInvestigationSignalsQuery } from "@/features/finance/services/finance-investigation-query"
import { useOpeningBalancesStateQuery } from "@/features/finance/services/finance-close-trust-query"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import { useFinanceOverviewScope } from "@/features/finance/overview/context/finance-overview-context"
import { formatCompactNumber } from "@/features/finance/overview/lib/overview-format"
import { OverviewRoutes, useOverviewNav } from "@/features/finance/overview/lib/overview-nav"
import { type FinanceSurfaceContext } from "@/features/finance/hooks/finance-context-rules"
import { WidgetWithheldState, useIsWithheld } from "@/features/finance/overview/widgets/widget-withheld-state"

type Severity = "critical" | "warning"

export interface AttentionIssue {
  key: string
  severity: Severity
  icon: LucideIcon
  count: number
  route: string
}

/**
 * Needs Attention — real, actionable finance issues, each linking to where it can be resolved. Current-state
 * (period-independent). Assembled from the existing partial signals (investigation signals + opening-balances
 * state); a single consolidated attention endpoint (plus negative-fund detection) is a deferred backend.
 */
/**
 * What needs a treasurer's attention (unset opening balances, missing evidence, voided or reversed entries), most
 * severe first. Shared by the dashboard widget and the phone's finance overview.
 */
export function useFinanceAttentionIssues({ context }: { context?: FinanceSurfaceContext } = {}) {
  const scope = useFinanceOverviewScope()

  /*
   * Investigation signals REFUSE for a branch whose figures are withheld (ADR-0024) — an amount-level tool
   * has no meaningful share. Asking anyway produced a red "could not load" card with a Retry button that
   * could never succeed. Decided from the board's context, so the request is not made at all.
   */
  const withheld = useIsWithheld(context)

  const signalsQuery = useFinanceInvestigationSignalsQuery(
    scope.workspaceId && !withheld
      ? {
          workspaceId: scope.workspaceId,
          branchId: scope.branchId ?? undefined,
          scope: scope.context === "parish" ? "PARISH" : "BRANCH",
        }
      : null,
  )
  const openingQuery = useOpeningBalancesStateQuery(
    scope.workspaceId ? { workspaceId: scope.workspaceId, branchId: scope.branchId } : null,
  )

  const issues = React.useMemo<AttentionIssue[] | undefined>(() => {
    if (!signalsQuery.data) return undefined
    const signals = signalsQuery.data
    const list: AttentionIssue[] = []

    const opening = openingQuery.data
    if (opening?.initialized) {
      const unset = opening.accounts.filter((account) => !account.set).length
      if (unset > 0) {
        list.push({ key: "openingBalances", severity: "critical", icon: ScaleIcon, count: unset, route: OverviewRoutes.openingBalances })
      }
    }
    if (signals.missingEvidence?.count > 0) {
      list.push({ key: "missingEvidence", severity: "warning", icon: FileWarningIcon, count: signals.missingEvidence.count, route: OverviewRoutes.investigations })
    }
    if (signals.voided?.count > 0) {
      list.push({ key: "voided", severity: "warning", icon: BanIcon, count: signals.voided.count, route: OverviewRoutes.investigations })
    }
    if (signals.reversed?.count > 0) {
      list.push({ key: "reversed", severity: "warning", icon: Undo2Icon, count: signals.reversed.count, route: OverviewRoutes.investigations })
    }

    const order: Record<Severity, number> = { critical: 0, warning: 1 }
    return list.sort((a, b) => order[a.severity] - order[b.severity])
  }, [signalsQuery.data, openingQuery.data])

  return { issues, withheld, signalsQuery, openingQuery }
}

export function NeedsAttentionWidget({ context }: { context?: FinanceSurfaceContext } = {}) {
  const t = useTranslations("finance.overview")
  const go = useOverviewNav()
  const { issues, withheld, signalsQuery, openingQuery } = useFinanceAttentionIssues({ context })

  useWidgetMenu({
    refresh: () => {
      void signalsQuery.refetch()
      void openingQuery.refetch()
    },
    viewDetails: go.canNavigateTo(OverviewRoutes.investigations) ? () => go(OverviewRoutes.investigations) : undefined,
  })

  // Withheld is a STATE, not a failure — render it before the boundary, which only knows loading/error/empty.
  if (withheld) return <WidgetWithheldState context={context} />

  return (
    <WidgetBoundary
      isLoading={signalsQuery.isLoading}
      isError={signalsQuery.isError}
      isFetching={signalsQuery.isFetching || openingQuery.isFetching}
      data={issues}
      isEmpty={(list) => list.length === 0}
      skeleton={<IssuesSkeleton />}
      errorTitle={t("widgets.needsAttention.error")}
      onRetry={() => void signalsQuery.refetch()}
      retryLabel={t("widgets.common.retry")}
      emptyIcon={<AlertTriangleIcon className="size-4" />}
      emptyTitle={t("widgets.needsAttention.empty.title")}
      emptyDescription={t("widgets.needsAttention.empty.description")}
    >
      {(list) => (
        <ul className="divide-y divide-border/60">
          {list.map((issue) => (
            <li key={issue.key}>
              {/*
                * Inert where the row cannot lead anywhere. `go` already refuses on the consolidated board
                * and while inspecting a branch from the parish, but a row that still hovers and takes a
                * click is a promise the widget cannot keep — and this widget is one of the defaults on the
                * inspection board, so it is the first thing a parish reader would try.
                */}
              <button
                type="button"
                onClick={() => go(issue.route)}
                disabled={!go.canNavigateTo(issue.route)}
                className={cn(
                  "flex w-full items-center gap-2.5 py-1.5 text-left",
                  go.canNavigateTo(issue.route) && "transition-colors hover:text-primary",
                )}
              >
                <issue.icon
                  className={cn("size-4 shrink-0", issue.severity === "critical" ? "text-destructive" : "text-amber-500")}
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1 truncate text-sm">{t(`widgets.needsAttention.issues.${issue.key}`)}</span>
                <Badge
                  variant={issue.severity === "critical" ? "destructive" : "secondary"}
                  className="shrink-0 tabular-nums"
                >
                  {formatCompactNumber(issue.count)}
                </Badge>
              </button>
            </li>
          ))}
        </ul>
      )}
    </WidgetBoundary>
  )
}

function IssuesSkeleton() {
  return (
    <div className="space-y-2.5 py-1">
      {[70, 58, 64].map((w, index) => (
        <div key={index} className="flex items-center gap-2.5">
          <div className="size-4 shrink-0 animate-pulse rounded bg-muted motion-reduce:animate-none" />
          <div className="h-3.5 animate-pulse rounded bg-muted motion-reduce:animate-none" style={{ width: `${w}%` }} />
          <div className="ml-auto h-4 w-6 animate-pulse rounded bg-muted motion-reduce:animate-none" />
        </div>
      ))}
    </div>
  )
}
